import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ValidationStatus } from '@dev/ee-api/ee-api-models';
import {
    palette,
    RobotIcon,
    SimplePieChart,
    Typography,
} from '@dev/ee-components';
import { reduce, includes } from 'lodash-es';
import styled from 'styled-components';

import i18n from '@/../i18n';
import { getContactsStore } from '@/store/contacts/actions';
import { CustomTooltip } from '../CustomTooltip';
import { SingleLegendItem } from '../SingleLegendItem';

const GraphLegend = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
`;

const ServiceBusyContainer = styled.div`
    text-align: center;
`;

const suppressedCounts = ['ServiceBusy', 'ValidationTime', 'UserStatus'];

export const validationColors = {
    ActiveCount: palette.indigo[6],
    InactiveCount: palette.indigo[5],
    InvalidCount: palette.red[7],
    UnsubscribeCount: palette.purple[6],
    TransactionalCount: palette.indigo[3],
    StaleCount: palette.gray[3],
    NotUploadedDuplicatedCount: palette.red[5],
    NotUploadedSyntaxCount: palette.orange[5],
    AbuseCount: palette.gray[6],
    BouncedCount: palette.red[7],
};

const statusValuesMapper = (allStatuses: ValidationStatus, status: keyof ValidationStatus) => {
    if (status === 'BouncedCount') {
        return allStatuses.BouncedCount - allStatuses.NotUploadedSyntaxCount;
    }
    return allStatuses[status];
};

export const statusNamesMapper = () => ({
    BouncedCount: i18n.t('invalid'),
    AbuseCount: i18n.t('complaint'),
    NotUploadedSyntaxCount: i18n.t('incorrectcontact'),
    ActiveCount: i18n.t('activecount'),
    InactiveCount: i18n.t('inactivecount'),
    InvalidCount: i18n.t('invalidcount'),
    UnsubscribeCount: i18n.t('unsubscribecount'),
    TransactionalCount: i18n.t('transactionalcount'),
    StaleCount: i18n.t('stalecount'),
    NotUploadedDuplicatedCount: i18n.t('notuploadedduplicatedcount'),
});

export const AnalyticsGraph = (): JSX.Element => {
    const { validationStatus } = useSelector(getContactsStore);
    const { t } = useTranslation();
    const graphData = [];
    const graphLegend = [];

    if (validationStatus?.ValidationStatus?.ServiceBusy === true) {
        return (
            <ServiceBusyContainer>
                <Typography variant="h1">{t('analysisisnotavailablerightnow')}</Typography>
                <RobotIcon width={590} />
            </ServiceBusyContainer>
        );
    }
    
    reduce(validationStatus?.ValidationStatus, (result, value, key) => {
        const mappedValue = statusValuesMapper(validationStatus?.ValidationStatus, key as keyof ValidationStatus);
        if (!includes(suppressedCounts, key) && mappedValue !== 0) {
            const statusNames = statusNamesMapper();
            graphData.push({
                name: key,
                value: mappedValue,
                color: validationColors[key]
            });
            graphLegend.push(<SingleLegendItem keyName={key} value={mappedValue} keyIdentifier={statusNames[key]} />);
        }
        return result;
    }, {});

    return (
        <div>
            <SimplePieChart
                data={graphData}
                height={600}
                width={1000}
                showPercentage={false}
                hideLabels={true}
                hideLegend={true}
                hideTooltip={false}
                innerRadius={165}
                outerRadius={225}
                activeShapeTransparency={0.5}
                fullWidth={true}
                customTooltip={<CustomTooltip />}
            />
            <GraphLegend>{graphLegend}</GraphLegend>
        </div>
    );
};